import styled from '@emotion/styled'
// Form Validation
import {object, string, oneOf, ref} from 'yup'

import { useParams, useLocation, useNavigate } from 'react-router-dom';

// useMutation
import { useMutation } from '@tanstack/react-query'

//axios
import axios from '../../axios'

// query params
import useQueryParams from '../../hooks/useQueryParams'

// handle Errors
import handleErrors from '../../utils/errorHandler'

// snackbar
import useSnackbarStore from '../../store/snackbar';

// Form Components
import { Form, Formik, FieldArray } from "formik";
import Field from '../../utils/FormikFields/Field';
import { Button, CircularProgress, FormHelperText } from '@mui/material';

// Form Field Icons
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';    
import AccountBoxRoundedIcon from '@mui/icons-material/AccountBoxRounded'; 
import AppSettingsAltRoundedIcon from '@mui/icons-material/AppSettingsAltRounded';

import Logo from '../../images/novosalon-logo.svg'

import { regex } from '../../utils/util';

import { Container, SubmitButton, Legal, Title, SubTitle, LogoBox } from './commonStyles'

import { Grid, Row } from '../../utils/Grid'


const StyledForm = styled(Form)`
  width: 100%;
`;


export const Setup = ( ) => {

    const params = useParams()

    const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

    const { slug, action } = useQueryParams()

    const { user_id: uid } = params

    const isSetup = action === 'setup'

    const navigate = useNavigate();

    const initialValues = {
      display_name: '',
      password: '',
      confirm_password: ''
    }

    let header = isSetup ? "Account Setup" : "Reset Your Password";
    let subheader = isSetup ? "Please set up your account" : "Please set up your password";

    let cta = isSetup ? "Setup Your Account" : "Reset Your Password";

    if (!isSetup) {
      delete initialValues.display_name
    }

    const schema = object().shape({
      display_name: isSetup ? string()
        .required('Display name is required.') : null,
      password: string().required('Password is required.')
      .matches(regex, 'Password must be eight characters with at least one uppercase letter and a number'),
      confirm_password: string()
      .oneOf([ref("password"), null], "Passwords must match")
    })

    const setup = async ({ userData, formikHelpers }) => {
      try {
        let url = isSetup ? `/auth/setup-account/${uid}` : `/auth/reset-password/${uid}?action=${action}`
        const { data } = await axios.post(url, {...userData, slug});
        return data;
      } catch (e) {
        throw e;
      }
    }

    const { mutateAsync, isPending } = useMutation({
      mutationKey: ["setup"],
      mutationFn: setup,
      onSuccess: (data, { formikHelpers }) => {
        console.log(data)
        formikHelpers.setSubmitting(false);
        showSnackbar("Successfully updated!", 'success');
        navigate('/login');
      },
      onError: (error, { formikHelpers }) => {
        let message = handleErrors(error, formikHelpers.setErrors);
        if (message) showSnackbar(message, 'error');
        formikHelpers.setSubmitting(false);
      }
    })

    const handleSubmit = async (info, formikHelpers) => {
      await mutateAsync({ 
        userData: info, 
        formikHelpers 
      })
    }

  return (
    <Container>
    <Title>
        {header}
      </Title>
      <SubTitle>{subheader}</SubTitle>
      <LogoBox>
        <img src={Logo} alt="logo graphic" />
      </LogoBox>
      <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit} enableReinitialize>
        {(propBag) => (
          <StyledForm>
          <Grid>
            {isSetup ? <Row>
              <Field name="display_name" size="large" label="Display Name" InputProps={{endAdornment: <AccountBoxRoundedIcon color="icon" />}} type="text" />
            </Row> : null}
            <Row>
              <Field name="password" size="large" label="Password" showSuccess type="password" InputProps={{endAdornment: <EmailRoundedIcon color="icon" />}} />
            </Row>
            <Row>
              <Field name="confirm_password" size="large" label="Confirm Password" InputProps={{endAdornment: <LockRoundedIcon color="icon" />}} type="password" />
            </Row>
            <Row>
              {propBag?.errors?._error && <FormHelperText error>* {propBag?.errors?._error}</FormHelperText>}
            </Row>
            <Row>
              <Button type="submit" fullWidth variant="contained" 
              sx={{ py: "1.5rem", mt: "1rem", gap: "1rem" }}
              loading={isPending ? true : false} 
              loadingPosition="start" size="large" startIcon={<AppSettingsAltRoundedIcon />}>{cta}</Button>
            </Row>
          </Grid>
        </StyledForm>
        )}
      </Formik>
      <Legal>
        &copy; Novosoft 2023 | All Rights Reserved
      </Legal>
    </Container>
  )
}
